import React, { useState, useEffect } from 'react';
import WalletSelector from '../common/WalletSelector';
import { CopyableMint } from '../common/CopyButton';
import '../../styles/enhanced-dashboard.css';
import { useTheme } from '../../contexts/ThemeContext';
const EnhancedDashboard = ({ 
  realTimeData, 
  activeSessions, 
  alerts, 
  tradeHistory,
  makeAuthenticatedRequest,
  setCurrentView 
}) => {
  const [portfolioData, setPortfolioData] = useState({
    totalValue: 0,
    totalPnL: 0,
    todayPnL: 0,
    winRate: 0
  });

  const { theme, isLight, isDark } = useTheme();

  // Calculate portfolio metrics
  useEffect(() => {
    if (activeSessions.length > 0) {
      const totalPnL = activeSessions.reduce((sum, session) => sum + (session.profitLoss || 0), 0);
      const winningTrades = tradeHistory.filter(trade => trade.profit_loss > 0).length;
      const totalTrades = tradeHistory.length;
      
      setPortfolioData({
        totalValue: realTimeData.totalPnL || 0,
        totalPnL: totalPnL,
        todayPnL: realTimeData.todayVolume || 0,
        winRate: totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0
      });
    }
  }, [activeSessions, tradeHistory, realTimeData]);

  const StatCard = ({ title, value, change, icon, color = 'blue', trend }) => (
    <div className={`stat-card ${color}`}
      style={{ backgroundColor: isLight ? '#f0f4f8' : '#1a1a1a', 
        color: isLight ? '#333' : '#fff' }}
    >
      <div className="stat-header">
        <div className="stat-icon">{icon}</div>
        <div className="stat-trend">
          {trend && (
            <span className={`trend-indicator ${trend > 0 ? 'up' : 'down'}`}>
              {trend > 0 ? '↗' : '↘'} {Math.abs(trend).toFixed(2)}%
            </span>
          )}
        </div>
      </div>
      <div className="stat-content">
        <strong className="stat-value" style={{color:color}}>{value}</strong>
        <p className="stat-title" >{title}</p>
        {change && (
          <div className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
          </div>
        )}
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon, action, color = 'primary' }) => (
    <div className={`quick-action-card ${color}`} onClick={action}>
      <div className="action-icon">{icon}</div>
      <div className="action-content">
        <strong style={{color: isDark?'grey':'black'}}>{title}</strong>
        <p>{description}</p>
      </div>
      <div className="action-arrow">→</div>
    </div>
  );

  // Navigation handlers
  const handleStartTradingBot = () => {
    if (setCurrentView) {
      setCurrentView('compact-trading');
    }
  };

  const handleManualTrade = () => {
    if (setCurrentView) {
      setCurrentView('compact-trading');
    }
  };

  const handleViewAnalytics = () => {
    if (setCurrentView) {
      setCurrentView('history');
    }
  };

  const handleEmergencyStop = () => {
    // This could trigger an API call to stop all trading
    console.log('Emergency stop triggered');
    // For now, just show an alert
    alert('Emergency stop functionality would be implemented here');
  };

  return (
    <div className="enhanced-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header-section">
        <div className="header-content">
          <div className="welcome-section">
            <h1>Welcome back! 👋</h1>
            <p>Here's what's happening with your trading portfolio today...</p>
          </div>
          <div className="header-actions">
            {/* <WalletSelector className="header-wallet-selector" /> */}
            <div className="market-status">
              <div className="market-indicator">
                <span className="status-dot active"></span>
                <span>Market Open</span>
              </div>
              <div className="sol-price">
                SOL: $183.50 <span className="price-change positive">+2.4%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <StatCard
          title="Portfolio Value"
          value={`$${portfolioData.totalValue.toLocaleString()}`}
          change={portfolioData.totalPnL}
          icon="💰"
          color="green"
          // trend={portfolioData.totalPnL}
        />
        <StatCard
          title="Active Sessions"
          value={activeSessions.length}
          icon="🤖"
          color="blue"
        />
        <StatCard
          title="Today's P&L"
          value={`$${portfolioData.todayPnL.toLocaleString()}`}
          change={5.2}
          icon="📈"
          color="purple"
          trend={5.2}
        />
        <StatCard
          title="Win Rate"
          value={`${portfolioData.winRate.toFixed(1)}%`}
          icon="🎯"
          color="orange"
        />
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content-grid">
        {/* Active Sessions Overview */}
        <div className="dashboard-card sessions-overview">
          <div className="card-header">
            <h3>🔄 Active Trading Sessions</h3>
            <button className="view-all-btn">View All</button>
          </div>
          <div className="card-content">
            {activeSessions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🤖</div>
                <h4>No Active Sessions</h4>
                <p>Start your first automated trading bot to see live sessions here.</p>
                <button className="start-trading-btn" onClick={handleStartTradingBot}>Start Trading Bot</button>
              </div>
            ) : (
              <div className="sessions-list">
                {activeSessions.slice(0, 3).map(session => (
                  <div key={session.sessionId} className="session-item">
                    <div className="session-info">
                      <div className="token-info">
                        <span className="token-symbol">{session.tokenSymbol || 'Unknown'}</span>
                        <div className="token-address">
                          <CopyableMint 
                            mintAddress={session.tokenMint}
                            truncate={true}
                            truncateLength={6}
                          />
                        </div>
                      </div>
                      <div className="session-metrics">
                        <div className="metric">
                          <span className="metric-label">P&L</span>
                          <span className={`metric-value ${session.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                            {session.profitLoss >= 0 ? '+' : ''}{session.profitLoss?.toFixed(2)}%
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Price</span>
                          <span className="metric-value">
                            {session.currentPrice?.toFixed(6)} SOL
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`session-status ${session.status}`}>
                      {session.status.replace('_', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="dashboard-card alerts-overview">
          <div className="card-header" >
            <strong style={{
              color: isLight ? 'black' : 'white',
            }}>🔔 Recent Alerts</strong>
            <span className="alert-count">{alerts.filter(a => !a.read).length} new</span>
          </div>
          <div className="card-content">
            {alerts.length === 0 ? (
              <div className="empty-state small">
                <div className="empty-icon">🔔</div>
                <p>No alerts yet</p>
              </div>
            ) : (
              <div className="alerts-list">
                {alerts.slice(0, 4).map(alert => (
                  <div key={alert.id} className={`alert-item ${alert.read ? 'read' : 'unread'}`}>
                    <div className="alert-icon">
                      {alert.type === 'stop_loss_executed' && '🛑'}
                      {alert.type === 'reentry_executed' && '🔄'}
                      {alert.type === 'trading_session_started' && '🚀'}
                      {alert.type === 'insufficient_funds' && '⚠️'}
                    </div>
                    <div className="alert-content">
                      <p className="alert-message">{alert.message}</p>
                      <span className="alert-time">
                        {new Date(alert.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card quick-actions">
          <div className="" style={{paddingBottom:'3%'}}>
            <strong style={{
              color: isLight ? '#333' : '#fff',
            }}>⚡ Quick Actions</strong>
          </div>
          <div className="card-content">
            <div className="actions-grid">
              <QuickActionCard
                title="Start Trading Bot"
                description="Launch automated trading"
                icon="🤖"
                color="primary"
                action={handleStartTradingBot}
              />
              <QuickActionCard
                title="Manual Trade"
                description="Execute trades manually"
                icon="⚡"
                color="secondary"
                action={handleManualTrade}
              />
              <QuickActionCard
                title="View Analytics"
                description="Performance insights"
                icon="📊"
                color="info"
                action={handleViewAnalytics}
              />
              <QuickActionCard
                title="Emergency Stop"
                description="Stop all trading"
                icon="🛑"
                color="danger"
                action={handleEmergencyStop}
              />
            </div>
          </div>
        </div>

        {/* Performance Chart */}
        <div className="dashboard-card performance-chart">
          <div className="card-header">
            <strong>📈 Performance Overview</strong>
            <div className="chart-controls">
              <button className="chart-btn active">24H</button>
              <button className="chart-btn">7D</button>
              <button className="chart-btn">30D</button>
            </div>
          </div>
          <div className="card-content">
            <div className="chart-placeholder">
              <div className="chart-visual">
                <div className="chart-line"></div>
                <div className="chart-points">
                  <div className="point" style={{left: '10%', bottom: '30%'}}></div>
                  <div className="point" style={{left: '25%', bottom: '45%'}}></div>
                  <div className="point" style={{left: '40%', bottom: '60%'}}></div>
                  <div className="point" style={{left: '55%', bottom: '40%'}}></div>
                  <div className="point" style={{left: '70%', bottom: '75%'}}></div>
                  <div className="point" style={{left: '85%', bottom: '65%'}}></div>
                </div>
              </div>
              <div className="chart-stats">
                <div className="stat">
                  <span className="stat-label">Best Day</span>
                  <span className="stat-value positive">+12.4%</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Avg. Daily</span>
                  <span className="stat-value">+2.1%</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Total Trades</span>
                  <span className="stat-value">{tradeHistory.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;